import React, { memo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  ReservationSlotOverride,
  ReservationSlotTemplate,
} from '../../../../domain/reservationSlots';
import { formatTemplateLabel } from '../formatTemplateLabel';
import { SettingsBlock, SettingsRow } from '../ReservationSlotManagement.styles';

/**
 * SettingsSummaryBlocks のProps型
 *
 * @property {readonly ReservationSlotTemplate[]} templates - テンプレート一覧（IDからラベルを引くために使う）
 * @property {string | null} defaultTemplateId - 全医師に適用するデフォルトテンプレートID
 * @property {readonly ReservationSlotOverride[]} overrides - 医師個別の上書き設定
 */
export interface SettingsSummaryBlocksProps {
  templates: readonly ReservationSlotTemplate[];
  defaultTemplateId: string | null;
  overrides: readonly ReservationSlotOverride[];
}

/**
 * テンプレートIDが解決できなかった場合の表示ラベル
 */
const UNKNOWN_TEMPLATE_LABEL = '（不明なテンプレート）';

/**
 * テンプレートIDから表示ラベルを引く
 *
 * @description
 * 該当テンプレートが見つからない場合は基本設定パネルと同じく「（不明なテンプレート）」を返す。
 *
 * @param {readonly ReservationSlotTemplate[]} templates - テンプレート一覧
 * @param {string} templateId - 対象テンプレートID
 * @returns {string} 「ピル標準（15分・2枠）」形式のラベル
 */
function resolveTemplateLabel(
  templates: readonly ReservationSlotTemplate[],
  templateId: string
): string {
  const template = templates.find((candidate) => candidate.id === templateId);
  return template === undefined ? UNKNOWN_TEMPLATE_LABEL : formatTemplateLabel(template);
}

/**
 * 基本設定・個別上書きの読み取り専用サマリ
 *
 * @description
 * 解放枠設定画面のSTEP2で、現在の基本設定（デフォルトテンプレート）と
 * 医師個別の上書き一覧を確認用に並べて表示する。編集操作は持たない。
 *
 * @param {SettingsSummaryBlocksProps} props - コンポーネントProps
 * @returns {React.ReactElement} 設定サマリブロック
 */
const SettingsSummaryBlocks: React.FC<SettingsSummaryBlocksProps> = memo(
  ({ templates, defaultTemplateId, overrides }) => {
    const theme = useTheme();
    const defaultTemplate =
      defaultTemplateId === null
        ? undefined
        : templates.find((candidate) => candidate.id === defaultTemplateId);

    return (
      <SettingsRow>
        {/* 基本設定 */}
        <SettingsBlock>
          <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.5 }}>🌐 基本設定</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5, lineHeight: 1.6 }}>
            個別上書きのない医師全員に適用されます
          </Typography>
          {defaultTemplateId === null ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>未設定</Typography>
          ) : (
            <Chip
              label={
                defaultTemplate === undefined
                  ? UNKNOWN_TEMPLATE_LABEL
                  : formatTemplateLabel(defaultTemplate)
              }
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.dark,
                fontWeight: 700,
                borderRadius: 1,
              }}
            />
          )}
        </SettingsBlock>

        {/* 個別上書き */}
        <SettingsBlock>
          <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.5 }}>👤 個別上書き</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5, lineHeight: 1.6 }}>
            基本設定と異なる医師のみ登録します
          </Typography>
          {overrides.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              個別上書きはありません
            </Typography>
          ) : (
            overrides.map((override) => (
              <Box
                key={override.doctorId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.25,
                  py: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{override.doctorName}</Typography>
                <Chip
                  label={resolveTemplateLabel(templates, override.templateId)}
                  size="small"
                  sx={{ fontWeight: 700, borderRadius: 1 }}
                />
              </Box>
            ))
          )}
        </SettingsBlock>
      </SettingsRow>
    );
  }
);

SettingsSummaryBlocks.displayName = 'SettingsSummaryBlocks';

export default SettingsSummaryBlocks;
